import { apiRequest } from './apiUtils';

export interface AffiliateLink {
  id: string;
  url: string;
  token: string;
  visitors: number;
  leads: number;
  conversions: number;
  affiliate_id?: string;
}

export interface Campaign {
  id: string;
  name: string;
  url: string;
  reward_type: string;
  commission_percent: number;
  commission_amount_cents: number | null;
  commission_amount_currency: string | null;
  minimum_payout_currency: string;
  minimum_payout_cents: number;
  days_before_referrals_expire: number;
  max_commission_period_months: number;
  max_commissions: number;
  days_until_commissions_are_due: number;
}

export interface Affiliate {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  state: string;
  links: AffiliateLink[];
  campaign: Campaign;
}

export interface AffiliateResponse {
  success: boolean;
  data?: Affiliate;
  error?: string;
  details?: any;
}

export interface CampaignResponse {
  success: boolean;
  data?: Campaign;
  error?: string;
}

export interface CreateAffiliateParams {
  first_name: string;
  last_name: string;
  email: string;
  influencerHandle: string;
}

/**
 * Create a new affiliate
 */
export const createAffiliate = async (params: CreateAffiliateParams, influencerId: string): Promise<AffiliateResponse> => {
  try {
    const response = await apiRequest<AffiliateResponse>({
      method: 'POST',
      endpoint: '/referrals/affiliates/create',
      data: params,
      influencerId,
    });
    return response;
  } catch (error) {
    console.error('Error creating affiliate:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create affiliate'
    };
  }
};

/**
 * Get an affiliate's details
 */
export const getAffiliate = async (affiliateId: string): Promise<AffiliateResponse> => {
  try {
    const response = await apiRequest<AffiliateResponse>({
      method: 'GET',
      endpoint: `/referrals/affiliates/${affiliateId}`
    });
    return response;
  } catch (error) {
    console.error('Error fetching affiliate:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch affiliate'
    };
  }
};

/**
 * Get campaign details
 */
export const getCampaign = async (campaignId: string): Promise<CampaignResponse> => {
  try {
    const response = await apiRequest<CampaignResponse>({
      method: 'GET',
      endpoint: `/referrals/campaigns/${campaignId}`
    });
    return response;
  } catch (error) {
    console.error('Error fetching campaign:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch campaign'
    };
  }
};

/**
 * Get default campaign details
 */
export const getDefaultCampaign = async (): Promise<CampaignResponse> => {
  try {
    const response = await apiRequest<CampaignResponse>({
      method: 'GET',
      endpoint: '/referrals/campaigns/default'
    });
    return response;
  } catch (error) {
    console.error('Error fetching default campaign:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch default campaign'
    };
  }
};
